"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import HeaderPublic from "@components/layout/HeaderPublic";
import FooterPublic from "@components/layout/FooterPublic";
import SpeakerAvatars from "@components/ui/SpeakerAvatars";
import ClientLogosMarquee from "@components/sections/ClientLogosMarquee";
import { trackGAEvent } from "@utils/ga4Tracking";
import pageData from "@data/landing-pages/geschaeftsprozesse-mit-ki-automatisieren.json";
import type { HeroVariantKey } from "../hero-variant";

const KURS_ROUTE = "/services/ki-digitalisierung-mittelstand";
const MEET_ROUTE = "/meet";
const LP_SOURCE = "geschaeftsprozesse-mit-ki-automatisieren";
const PAGE_TRACKING_ID = "lp-geschaeftsprozesse-mit-ki-automatisieren";

/* Attribution-Plumbing (Brief §7): Der Klick wird auf dieser Seite bezahlt,
 * die Conversion entsteht auf der Kursseite. Ohne Parameter-Weitergabe ist
 * der Cluster in den Ads-Daten unsichtbar. Gleiche Keys + gleicher
 * sessionStorage-Key wie KursClient, damit die Kursseite die Werte übernimmt. */
const ATTRIBUTION_KEYS = [
  "gclid",
  "wbraid",
  "gbraid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "matched_keyword",
  "ad_group",
  "campaign_id",
] as const;

const ATTRIBUTION_STORAGE_KEY = "dbi_lp_attribution";
const FIRST_TOUCH_KEY = "dbi_first_touch_date";
const SESSION_COUNT_KEY = "dbi_session_count";
const SESSION_FLAG_KEY = "dbi_session_counted";

/* Designsystem der Seite (aus dem Design-Referenz-HTML):
 * Kanit medium für Headlines, Archivo (Site-Default) für Copy, nummerierte
 * Eyebrows mit orangener Ziffer, dunkle Gradient-Panels, segmentierte Listen
 * mit 1px-Fugen statt Einzelkarten. Farbwerte bewusst lokal gehalten, damit
 * globale Tokens unangetastet bleiben. */
const INK = "#424a63"; // Fließtext
const INK_SOFT = "#5f6883"; // sekundärer Text
const INK_MUTED = "#8d96ad"; // Eyebrows, Labels
const LINE = "#e6eaf3"; // Kartenfugen
const LINE_SOFT = "#eceef4"; // Sektionstrenner
const LINE_BLUE = "#d6dff0"; // Linien auf hellblau
const BG_ALT = "#f6f7fa"; // alternierender Sektionshintergrund
const BLUE_SOFT = "#a8b8db"; // Text auf dunklem Blau
const BLUE_PALE = "#d6dff0"; // hellerer Text auf dunklem Blau
const ACCENT_DEEP = "#d97a1f"; // dunkleres Orange für Labels auf Weiß
const GREEN = "#1c8c5b"; // Häkchen

const darkPanel = "bg-gradient-to-br from-[#003188] to-[#001f5c]";
const container = "max-w-[1180px] mx-auto px-5 md:px-10";

function Eyebrow({ num, label, className = "" }: { num?: string; label: string; className?: string }) {
  return (
    <p
      className={`text-[11.5px] font-semibold uppercase tracking-[0.16em] ${className}`}
      style={{ color: INK_MUTED }}
    >
      {num && <span className="text-abis-orange">{num}</span>}
      {num && " · "}
      {label}
    </p>
  );
}

function SectionTitle({
  children,
  className = "",
  dark = false,
}: {
  children: React.ReactNode;
  className?: string;
  dark?: boolean;
}) {
  return (
    <h2
      className={`font-kanit font-medium text-[1.75rem] md:text-[2.5rem] leading-[1.15] tracking-[-0.01em] mt-3.5 ${
        dark ? "text-white" : "text-abis-primary"
      } ${className}`}
    >
      {children}
    </h2>
  );
}

/* H1-Highlight: Der Teil nach dem ersten Doppelpunkt wird in hellerem Blau
   abgesetzt — funktioniert für jede Hero-Variante mit Doppelpunkt, sonst
   bleibt die Zeile einfarbig. */
function HeroTitle({ title }: { title: string }) {
  const splitAt = title.indexOf(":");
  if (splitAt === -1) {
    return <>{title}</>;
  }
  return (
    <>
      {title.slice(0, splitAt + 1)} <span className="text-[#2e57a8]">{title.slice(splitAt + 1).trim()}</span>
    </>
  );
}

/* Die Vier-Stufen-Grafik im Hero: nummerierte Timeline auf dunklem Panel,
   Stufe 1 und 2 hervorgehoben (die übersprungenen Pflicht-Stufen). */
function StufenGrafik() {
  const steps = pageData.stufen.steps;
  return (
    <aside className={`${darkPanel} rounded-3xl p-7 md:p-8 shadow-[0_24px_60px_rgba(0,31,92,0.28)]`}>
      <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em]" style={{ color: BLUE_SOFT }}>
        {pageData.hero.graphic.eyebrow}
      </p>
      <ol className="mt-5 list-none p-0 m-0">
        {steps.map((step, index) => {
          const highlighted = index < 2;
          const last = index === steps.length - 1;
          return (
            <li key={step.number} className="grid grid-cols-[44px_1fr] gap-4">
              <div className="flex flex-col items-center gap-1.5">
                <div
                  className={`w-[38px] h-[38px] rounded-xl font-kanit font-medium text-lg flex items-center justify-center ${
                    highlighted
                      ? "bg-abis-orange text-abis-primary"
                      : "bg-white/10 border border-white/20 text-white"
                  }`}
                >
                  {step.number}
                </div>
                {!last && (
                  <div
                    className="flex-1 w-0.5"
                    style={{
                      background:
                        index === 0
                          ? "linear-gradient(180deg,#ffa346,rgba(255,255,255,0.18))"
                          : "rgba(255,255,255,0.18)",
                    }}
                  />
                )}
              </div>
              <div className={last ? "" : "pb-6"}>
                <p className={`font-kanit text-[19px] ${highlighted ? "text-abis-orange" : "text-white"}`}>
                  {step.title}
                </p>
                <p
                  className="text-[14.5px] leading-[1.55] mt-1"
                  style={{ color: highlighted ? BLUE_PALE : BLUE_SOFT }}
                >
                  {step.question}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
      <p
        className="mt-5 pt-5 border-t border-white/15 text-sm leading-[1.55]"
        style={{ color: BLUE_SOFT }}
      >
        {pageData.hero.graphic.caption}
      </p>
    </aside>
  );
}

interface AutomatisierungClientProps {
  variant: HeroVariantKey;
  nextCourseLabel: string;
  /** GuideReadNext for this route, rendered by the server page. */
  guideSlot: React.ReactNode;
}

export default function AutomatisierungClient({
  variant,
  nextCourseLabel,
  guideSlot,
}: AutomatisierungClientProps) {
  const hero = pageData.heroVariants[variant];
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [passthroughQuery, setPassthroughQuery] = useState("");

  const heroRef = useRef<HTMLElement>(null);
  const stufenRef = useRef<HTMLElement>(null);
  const firedEvents = useRef<Set<string>>(new Set());

  const fireOnce = useCallback((eventName: string) => {
    if (firedEvents.current.has(eventName)) return;
    firedEvents.current.add(eventName);
    trackGAEvent(eventName, { page: PAGE_TRACKING_ID, category: "lp_engagement" });
  }, []);

  // Attribution: URL-Parameter in sessionStorage mergen (gleicher Key wie die
  // Kursseite — deren Formular übernimmt lp_source & Co. aus dem Storage) und
  // als Query-String für alle internen CTAs bereitstellen (Fallback für
  // neue Tabs / blockierten Storage).
  useEffect(() => {
    let query = `lp_source=${LP_SOURCE}&lp_ref_variant=${variant}`;
    try {
      const params = new URLSearchParams(window.location.search);
      const stored: Record<string, string> = JSON.parse(
        sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) ?? "{}"
      );
      const passthrough = new URLSearchParams();
      passthrough.set("lp_source", LP_SOURCE);
      passthrough.set("lp_ref_variant", variant);
      ATTRIBUTION_KEYS.forEach((key) => {
        const value = params.get(key) ?? stored[key];
        if (value) {
          stored[key] = value;
          passthrough.set(key, value);
        }
      });
      stored.lp_source = LP_SOURCE;
      stored.lp_ref_variant = variant;
      stored.landing_page_path = stored.landing_page_path ?? window.location.pathname;
      sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(stored));

      if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
        localStorage.setItem(FIRST_TOUCH_KEY, new Date().toISOString());
      }
      if (!sessionStorage.getItem(SESSION_FLAG_KEY)) {
        sessionStorage.setItem(SESSION_FLAG_KEY, "1");
        const count = Number(localStorage.getItem(SESSION_COUNT_KEY) ?? "0") + 1;
        localStorage.setItem(SESSION_COUNT_KEY, String(count));
      }
      query = passthrough.toString();
    } catch {
      // Storage unavailable (private mode) — URL-Pass-Through bleibt trotzdem.
    }
    setPassthroughQuery(query);
  }, [variant]);

  // Sticky-Bar ab Ende Hero.
  useEffect(() => {
    const heroEl = heroRef.current;
    if (!heroEl) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(heroEl);
    return () => observer.disconnect();
  }, []);

  // Micro-Conversions (Brief §7.3): Vier-Stufen-Sektion gesehen + 75 % Scroll.
  useEffect(() => {
    const stufenEl = stufenRef.current;
    let observer: IntersectionObserver | undefined;
    if (stufenEl) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) fireOnce("view_stufen");
        },
        { threshold: 0.5 }
      );
      observer.observe(stufenEl);
    }

    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable > 0 && window.scrollY / scrollable > 0.75) {
        fireOnce("scroll_75");
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [fireOnce]);

  // Kurs-Hero ?v=prozesse: Message Match für den Automatisierungs-Cluster
  // auf der Kursseite (deren Prozess-Variante).
  const kursHref = `${KURS_ROUTE}?v=prozesse${passthroughQuery ? `&${passthroughQuery}` : ""}`;
  const meetHref = `${MEET_ROUTE}${passthroughQuery ? `?${passthroughQuery}` : ""}`;

  // Hauptkennzahl der Seite (Brief §7.3): CTA-Klickrate auf den Kurs.
  const trackKursCta = (position: string) => {
    trackGAEvent("cta_kurs_klick", {
      page: PAGE_TRACKING_ID,
      category: "lp_conversion",
      customParams: { lp_variant: variant, cta_position: position },
    });
  };

  const trackMeetCta = (position: string) => {
    trackGAEvent("cta_erstgespraech_klick", {
      page: PAGE_TRACKING_ID,
      category: "lp_conversion",
      customParams: { lp_variant: variant, cta_position: position },
    });
  };

  const primaryCtaClasses =
    "inline-flex items-center justify-center gap-3 bg-abis-orange text-abis-primary font-semibold rounded-[14px] shadow-[0_10px_24px_rgba(217,122,31,0.26)] hover:bg-[#f08a2c] transition-colors";
  const checkRow = "grid grid-cols-[auto_1fr] gap-2.5 text-[15px] leading-normal text-white";

  return (
    <div className="bg-white overflow-x-hidden" style={{ color: INK }}>
      <HeaderPublic />

      <main className="pt-28 md:pt-36">
        {/* HERO — links Copy + CTAs, rechts Vier-Stufen-Grafik */}
        <section
          ref={heroRef}
          className="bg-gradient-to-b from-white to-[#f6f7fa] border-b"
          style={{ borderColor: LINE_SOFT }}
        >
          <div className={`${container} pt-12 pb-10 md:pt-[88px] md:pb-[72px] grid md:grid-cols-[1.15fr_0.85fr] gap-10 md:gap-[72px] items-start`}>
            <div>
              <span
                className="inline-flex items-center gap-2.5 border bg-white text-abis-primary rounded-full px-4 py-[7px] text-[11.5px] font-semibold uppercase tracking-[0.14em]"
                style={{ borderColor: LINE_BLUE }}
              >
                {pageData.hero.badge}
              </span>
              <h1 className="font-kanit font-medium text-[2.1rem] md:text-[3.5rem] leading-[1.08] tracking-[-0.015em] text-abis-primary mt-7 [text-wrap:pretty]">
                <HeroTitle title={hero.title} />
              </h1>
              <p className="text-lg md:text-[19px] leading-relaxed mt-6 max-w-[34em] [text-wrap:pretty]" style={{ color: "#2a3047" }}>
                {hero.paragraphs[0]}
              </p>
              {hero.paragraphs.slice(1).map((paragraph) => (
                <p key={paragraph} className="text-[17px] leading-[1.7] mt-5 max-w-[36em] [text-wrap:pretty]" style={{ color: INK_SOFT }}>
                  {paragraph}
                </p>
              ))}
              {/* flex-nowrap statt flex-wrap: die beiden CTAs bleiben auf
                  jeder Breite horizontal nebeneinander (schrumpfen per
                  responsivem Padding/Text statt untereinander zu fallen). */}
              <div className="flex flex-nowrap gap-2.5 sm:gap-3.5 mt-9">
                <Link
                  href={kursHref}
                  onClick={() => trackKursCta("hero")}
                  className={`${primaryCtaClasses} flex-1 sm:flex-none whitespace-nowrap text-[13.5px] sm:text-base px-4 sm:px-7 py-3.5 sm:py-[17px]`}
                >
                  {pageData.hero.primaryCta} <span aria-hidden="true">→</span>
                </Link>
                <Link
                  href={meetHref}
                  onClick={() => trackMeetCta("hero")}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-3 whitespace-nowrap bg-white text-abis-primary font-semibold text-[13.5px] sm:text-base px-4 sm:px-7 py-3.5 sm:py-[17px] rounded-[14px] border-[1.5px] hover:border-abis-primary transition-colors"
                  style={{ borderColor: LINE_BLUE }}
                >
                  {pageData.hero.secondaryCta} <span aria-hidden="true">→</span>
                </Link>
              </div>
              <ul className="flex flex-wrap gap-x-7 gap-y-2 mt-8 text-sm list-none p-0 m-0" style={{ color: INK_SOFT }}>
                {pageData.hero.trustItems.map((item) => (
                  <li key={item} className="inline-flex items-center gap-2">
                    <span className="font-bold" style={{ color: GREEN }} aria-hidden="true">
                      ✓
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <StufenGrafik />
          </div>

          {/* AEO-Definitionsblock — Label + kursive Definition statt grauer Box */}
          <div className={`${container} pb-14 md:pb-16`}>
            <div
              id="definition"
              className="grid md:grid-cols-[auto_1fr] gap-4 md:gap-7 items-start border-t pt-7"
              style={{ borderColor: LINE_BLUE }}
            >
              <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-abis-primary pt-1">
                Definition
              </p>
              <p className="m-0 text-base leading-[1.7] italic max-w-[80ch]" style={{ color: INK_SOFT }}>
                {pageData.hero.aeoDefinition}
              </p>
            </div>
          </div>
        </section>

        {/* 01 — DAS PROBLEM */}
        <section id="hakt" className="border-b" style={{ backgroundColor: BG_ALT, borderColor: LINE_SOFT }}>
          <div className={`${container} py-16 md:py-[104px]`}>
            <Eyebrow num="01" label={pageData.problem.eyebrow} />
            <SectionTitle className="max-w-[22em]">{pageData.problem.title}</SectionTitle>
            <p className="text-lg leading-[1.65] mt-5 max-w-[62ch]" style={{ color: INK }}>
              {pageData.problem.intro}
            </p>

            <div className="grid md:grid-cols-[1.25fr_0.75fr] gap-8 md:gap-12 mt-10 md:mt-[52px] items-start">
              {/* Segmentierte Zitatliste mit warmem Fazit-Panel */}
              <div
                className="grid gap-px rounded-[20px] overflow-hidden border"
                style={{ backgroundColor: LINE, borderColor: "#d9dde8" }}
              >
                {pageData.problem.items.map((item, index) => (
                  <div key={item.quote} className="bg-white px-7 py-6 md:px-[30px] md:py-[26px]">
                    <p
                      className="text-[11px] font-semibold uppercase tracking-[0.16em] mb-2"
                      style={{ color: INK_MUTED }}
                    >
                      Satz {index + 1} im Projektstart
                    </p>
                    <p className="font-kanit text-[20px] md:text-[22px] leading-[1.3] text-abis-primary">
                      {item.quote}
                    </p>
                    <p className="text-base leading-relaxed mt-2.5" style={{ color: INK_SOFT }}>
                      {item.answer}
                    </p>
                  </div>
                ))}
                <div className="bg-[#fff8ee] px-7 py-6 md:px-[30px] grid gap-3">
                  {pageData.problem.chips.map((chip) => (
                    <p
                      key={chip}
                      className="grid grid-cols-[auto_1fr] gap-3 items-start text-[15.5px] m-0"
                      style={{ color: "#2a3047" }}
                    >
                      <span className="font-bold" style={{ color: ACCENT_DEEP }} aria-hidden="true">
                        →
                      </span>
                      <span>{chip.replace(/ → /g, " — ")}</span>
                    </p>
                  ))}
                </div>
              </div>

              <div className="relative rounded-[20px] overflow-hidden shadow-[0_18px_44px_rgba(0,31,92,0.14)] self-stretch min-h-[280px] md:min-h-[340px]">
                <Image
                  src={pageData.problem.image.src}
                  alt={pageData.problem.image.alt}
                  fill
                  sizes="(max-width: 768px) 100vw, 440px"
                  className="object-cover object-[center_30%]"
                />
              </div>
            </div>

            {/* Pain: Scheiterquote + neue Regeln seit Sommer 2026 */}
            <div className={`${darkPanel} mt-11 rounded-3xl px-8 py-10 md:px-12 md:py-11 grid md:grid-cols-[0.8fr_1.2fr] gap-8 md:gap-12 items-center shadow-[0_24px_56px_rgba(0,31,92,0.24)]`}>
              <div>
                <p className="font-kanit font-medium text-[56px] md:text-[76px] leading-none tracking-[-0.02em] text-abis-orange">
                  {pageData.problem.painStat.value}
                </p>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] mt-3" style={{ color: BLUE_SOFT }}>
                  {pageData.problem.painStat.label}
                </p>
              </div>
              <div>
                <p className="m-0 text-lg leading-relaxed text-white">{pageData.problem.painStat.text}</p>
                <p className="mt-4 text-[15.5px] leading-relaxed" style={{ color: BLUE_SOFT }}>
                  {pageData.problem.painStat.regulation}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 02 — DBI-STORY: der eigene Use Case, früh auf der Seite */}
        <section id="story" className="bg-white border-b" style={{ borderColor: LINE_SOFT }}>
          <div className={`${container} py-16 md:py-[104px]`}>
            <Eyebrow num="02" label={pageData.story.eyebrow} />
            <SectionTitle className="max-w-[28em]">{pageData.story.title}</SectionTitle>
            <p className="text-lg leading-[1.65] mt-5 max-w-[62ch]" style={{ color: INK }}>
              {pageData.story.lede}
            </p>

            {/* Beinahe-Scheitern: drei Karten mit orangener Oberkante */}
            <h3 className="font-kanit font-normal text-[22px] text-abis-primary mt-12 mb-5">
              {pageData.story.fails.title}
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {pageData.story.fails.items.map((item, index) => (
                <article
                  key={item.title}
                  className="bg-white border border-t-[3px] border-t-abis-orange rounded-[18px] px-6 py-7 md:px-[26px] flex flex-col shadow-[0_10px_28px_rgba(0,49,136,0.05)]"
                  style={{ borderLeftColor: LINE, borderRightColor: LINE, borderBottomColor: LINE }}
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: INK_MUTED }}>
                    Moment 0{index + 1}
                  </p>
                  <h4 className="font-kanit font-normal text-[23px] leading-[1.25] text-abis-primary mt-2 mb-3">
                    {item.title}
                  </h4>
                  <p className="m-0 text-[15.5px] leading-[1.62] flex-1" style={{ color: INK_SOFT }}>
                    {item.text}
                  </p>
                  <p className="mt-5 pt-4 border-t text-[15px] leading-[1.55] text-abis-primary font-semibold" style={{ borderColor: LINE_SOFT }}>
                    {item.fix}
                  </p>
                </article>
              ))}
            </div>

            {/* Gründerzitat — Panel mit Portrait */}
            <figure
              className="mt-10 md:mt-14 m-0 rounded-3xl px-8 py-9 md:px-12 md:py-11 grid md:grid-cols-[1fr_auto] gap-8 md:gap-10 items-center"
              style={{ backgroundColor: BG_ALT }}
            >
              <div>
                <blockquote className="m-0 font-kanit font-normal text-[21px] md:text-[27px] leading-[1.38] text-abis-primary [text-wrap:pretty]">
                  „{pageData.story.quote.text}“
                </blockquote>
                <figcaption className="mt-5 text-[15px]" style={{ color: INK_SOFT }}>
                  <span className="text-abis-primary font-semibold">{pageData.story.quote.author}</span> ·{" "}
                  {pageData.story.quote.role}
                </figcaption>
              </div>
              <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-[3px] border-white shadow-[0_12px_28px_rgba(0,31,92,0.16)] shrink-0">
                <Image
                  src={pageData.story.quote.image}
                  alt={`Portrait ${pageData.story.quote.author}`}
                  fill
                  sizes="112px"
                  className="object-cover object-top"
                />
              </div>
            </figure>

            {/* Ergebnis-Zahlen — dunkles Panel mit Quellenzeile */}
            <div className={`${darkPanel} mt-10 md:mt-14 rounded-3xl p-3 pb-0 shadow-[0_24px_56px_rgba(0,31,92,0.22)]`}>
              <p className="px-6 pt-6 pb-1.5 md:px-9 md:pt-7 text-[11.5px] font-semibold uppercase tracking-[0.16em] m-0" style={{ color: BLUE_SOFT }}>
                {pageData.story.stats.title}
              </p>
              <div className="grid md:grid-cols-3 px-3 pb-3">
                {pageData.story.stats.items.map((stat, index) => (
                  <div
                    key={stat.label}
                    className={`px-6 py-5 pb-7 ${index > 0 ? "border-t md:border-t-0 md:border-l border-white/15" : ""}`}
                  >
                    <p className="font-kanit font-medium text-[44px] md:text-[52px] leading-none text-abis-orange m-0">
                      {stat.value}
                    </p>
                    <p className="text-[15px] font-semibold text-white mt-2.5 m-0">{stat.label}</p>
                    <p className="mt-2.5 mb-0 text-[14.5px] leading-relaxed" style={{ color: BLUE_SOFT }}>
                      {stat.text}
                    </p>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/15 px-6 py-4 md:px-9 md:py-[18px] flex flex-wrap gap-x-6 gap-y-2 items-center text-[13.5px]" style={{ color: BLUE_SOFT }}>
                <span>{pageData.story.stats.source}</span>
                <Link
                  href={meetHref}
                  onClick={() => trackMeetCta("story_stats")}
                  className="text-abis-orange font-semibold md:ml-auto hover:text-[#f08a2c] transition-colors"
                >
                  {pageData.story.stats.sourceCtaLabel} →
                </Link>
              </div>
            </div>

            {/* Ehrlichkeits-Hinweis */}
            <p className="mt-6 rounded-2xl border bg-[#fff8ee] px-6 py-5 md:px-7 text-base leading-[1.65] m-0" style={{ borderColor: "#ffe1bd", color: "#2a3047" }}>
              {pageData.story.stats.honesty}
            </p>

            {/* Referenzkunden */}
            <div className="mt-14 md:mt-16 text-center">
              <Eyebrow label={pageData.story.clients.eyebrow} className="text-center" />
              <p className="font-kanit text-2xl text-abis-primary mt-2.5 m-0">
                {pageData.story.clients.title}
              </p>
              <p className="text-base leading-relaxed mt-2.5 mx-auto max-w-[60ch]" style={{ color: INK_SOFT }}>
                {pageData.story.clients.lede}
              </p>
            </div>
            <div className="mt-7">
              <ClientLogosMarquee />
            </div>
          </div>
        </section>

        {/* 03 — DIE REIHENFOLGE (Kernsektion, HowTo-Schema in page.tsx) */}
        <section
          ref={stufenRef}
          id="reihenfolge"
          className="border-b scroll-mt-8"
          style={{ backgroundColor: BG_ALT, borderColor: LINE_SOFT }}
        >
          <div className={`${container} py-16 md:py-[104px]`}>
            <Eyebrow num="03" label={pageData.stufen.eyebrow} />
            <SectionTitle>{pageData.stufen.title}</SectionTitle>
            <p className="text-lg leading-[1.65] mt-5 max-w-[60ch]" style={{ color: INK }}>
              {pageData.stufen.lede}
            </p>

            {/* Verbundene Vier-Stufen-Karte: 1 & 2 dunkel (Pflicht), 3 & 4 hell */}
            <div
              className="mt-10 md:mt-12 grid md:grid-cols-4 rounded-[22px] overflow-hidden border bg-white shadow-[0_14px_36px_rgba(0,49,136,0.06)]"
              style={{ borderColor: LINE }}
            >
              {pageData.stufen.steps.map((step, index) => {
                const mandatory = index < 2;
                return (
                  <div
                    key={step.number}
                    className={`px-6 pt-7 pb-8 md:px-[26px] ${
                      mandatory ? (index === 0 ? "bg-abis-primary" : "bg-[#002a76]") : "bg-white md:border-l"
                    }`}
                    style={mandatory ? undefined : { borderColor: LINE }}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`w-[34px] h-[34px] rounded-[10px] font-kanit text-[17px] flex items-center justify-center ${
                          mandatory ? "bg-abis-orange text-abis-primary" : "bg-[#ecf1fa] text-abis-primary"
                        }`}
                      >
                        {step.number}
                      </span>
                      <span
                        className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${
                          mandatory ? "text-abis-orange" : ""
                        }`}
                        style={mandatory ? undefined : { color: INK_MUTED }}
                      >
                        Stufe {step.number}
                        {mandatory && " · Pflicht"}
                      </span>
                    </div>
                    <h3 className={`font-kanit font-normal text-[23px] mt-4 mb-1.5 ${mandatory ? "text-white" : "text-abis-primary"}`}>
                      {step.title}
                    </h3>
                    <p className={`text-[15px] font-semibold leading-[1.45] m-0 ${mandatory ? "text-abis-orange" : "text-[#2e57a8]"}`}>
                      {step.question}
                    </p>
                    <p className="mt-3 mb-0 text-[15px] leading-relaxed" style={{ color: mandatory ? BLUE_PALE : INK_SOFT }}>
                      {step.text}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="mt-9 flex flex-wrap gap-7 items-center">
              <p className="m-0 flex-1 basis-[420px] text-[17px] leading-[1.65]" style={{ color: INK }}>
                Wie Sie Stufe 1 aufsetzen, zeigt unsere{" "}
                <Link
                  href="/services/business-intelligence-beratung"
                  className="font-semibold text-abis-primary border-b border-abis-orange hover:text-[#d97a1f] transition-colors"
                >
                  Business Intelligence Beratung
                </Link>{" "}
                — oder in kompakter Form der kostenfreie Kurs.
              </p>
              <Link
                href={kursHref}
                onClick={() => trackKursCta("stufen")}
                className={`${primaryCtaClasses} text-base px-[26px] py-4`}
              >
                {pageData.stufen.ctaLabel} <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </section>

        {/* 04 — WAS SICH KONKRET AUTOMATISIEREN LÄSST */}
        <section className="bg-white border-b" style={{ borderColor: LINE_SOFT }}>
          <div className={`${container} py-16 md:py-[104px]`}>
            <div className="grid md:grid-cols-[1fr_0.85fr] gap-10 md:gap-16 items-start">
              <div>
                <Eyebrow num="04" label={pageData.bereiche.eyebrow} />
                <SectionTitle>{pageData.bereiche.title}</SectionTitle>
                <p className="text-lg leading-[1.65] mt-5" style={{ color: INK }}>
                  {pageData.bereiche.lede}
                </p>

                <div
                  className="mt-9 grid gap-px rounded-[18px] overflow-hidden border"
                  style={{ backgroundColor: LINE, borderColor: LINE }}
                >
                  {pageData.bereiche.cards.map((card) => (
                    <div
                      key={card.title}
                      className="bg-white px-7 py-6 md:px-7 md:py-[26px] grid md:grid-cols-[1fr_auto] gap-5 items-end hover:bg-[#f6f7fa] transition-colors"
                    >
                      <div>
                        <h3 className="font-kanit font-normal text-[22px] text-abis-primary m-0">{card.title}</h3>
                        <p className="mt-2 mb-0 text-[15.5px] leading-[1.62] max-w-[52ch]" style={{ color: INK_SOFT }}>
                          {card.text}
                        </p>
                      </div>
                      <Link
                        href={card.href}
                        className="text-[15px] font-semibold text-abis-primary whitespace-nowrap hover:text-[#d97a1f] transition-colors"
                      >
                        {card.linkLabel} →
                      </Link>
                    </div>
                  ))}
                </div>
              </div>

              <div className="md:sticky md:top-28 grid gap-6">
                <div className="relative rounded-[20px] overflow-hidden shadow-[0_18px_44px_rgba(0,31,92,0.14)] aspect-[16/10]">
                  <Image
                    src={pageData.bereiche.image.src}
                    alt={pageData.bereiche.image.alt}
                    fill
                    sizes="(max-width: 768px) 100vw, 460px"
                    className="object-cover"
                  />
                </div>
                <div className="rounded-[18px] px-6 py-6 md:px-[26px]" style={{ backgroundColor: BG_ALT }}>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] m-0" style={{ color: INK_MUTED }}>
                    Klarstellung
                  </p>
                  <p className="mt-2 mb-0 text-[15.5px] leading-[1.65]" style={{ color: "#2a3047" }}>
                    {pageData.bereiche.exclusion}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 05 — PRODUKTION (trägt ?v=produktion) */}
        <section id="produktion" className="border-b" style={{ backgroundColor: BG_ALT, borderColor: LINE_SOFT }}>
          <div className={`${container} py-16 md:py-24`}>
            <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <Eyebrow num="05" label={pageData.produktion.eyebrow} />
                <SectionTitle className="md:text-4xl">{pageData.produktion.title}</SectionTitle>
                {pageData.produktion.paragraphs.map((paragraph, index) => (
                  <p
                    key={paragraph}
                    className={`text-[17px] leading-[1.7] ${index === 0 ? "mt-5" : "mt-4"}`}
                    style={{ color: INK }}
                  >
                    {paragraph}
                  </p>
                ))}
                <div className="flex flex-wrap gap-2.5 mt-6">
                  {pageData.produktion.chips.map((chip) => (
                    <span
                      key={chip}
                      className="border bg-white rounded-full px-4 py-2 text-sm font-semibold text-abis-primary"
                      style={{ borderColor: LINE_BLUE }}
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              </div>
              <div className="relative rounded-[22px] overflow-hidden shadow-[0_20px_50px_rgba(0,31,92,0.16)] aspect-[3/2]">
                <Image
                  src={pageData.produktion.image.src}
                  alt={pageData.produktion.image.alt}
                  fill
                  sizes="(max-width: 768px) 100vw, 550px"
                  className="object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* 06 — REFERENZPROJEKTE: Herausforderung → Lösung → Kundenstimme.
            Steht direkt vor der CTA-Sektion: Beweis unmittelbar vor der Bitte. */}
        <section id="cases" className="bg-white border-b" style={{ borderColor: LINE_SOFT }}>
          <div className={`${container} py-16 md:py-[104px]`}>
            <Eyebrow num="06" label={pageData.cases.eyebrow} />
            <SectionTitle className="max-w-[24em]">{pageData.cases.title}</SectionTitle>
            <p className="text-lg leading-[1.65] mt-5 max-w-[62ch]" style={{ color: INK }}>
              {pageData.cases.lede}
            </p>

            <div className="mt-10 md:mt-12 grid md:grid-cols-3 gap-6">
              {pageData.cases.items.map((item) => (
                <article
                  key={item.href}
                  className="flex flex-col rounded-[18px] border overflow-hidden bg-white shadow-[0_10px_28px_rgba(0,49,136,0.05)]"
                  style={{ borderColor: LINE }}
                >
                  <div className="px-6 pt-7 pb-6 md:px-[26px] flex-1 flex flex-col">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] m-0" style={{ color: INK_MUTED }}>
                      {item.industry}
                    </p>
                    <h3 className="font-kanit font-normal text-[23px] leading-[1.25] text-abis-primary mt-2 mb-5">
                      {item.title}
                    </h3>

                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] m-0" style={{ color: ACCENT_DEEP }}>
                      Herausforderung
                    </p>
                    <p className="mt-1.5 mb-4 text-[15px] leading-[1.62]" style={{ color: INK_SOFT }}>
                      {item.challenge}
                    </p>

                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] m-0" style={{ color: ACCENT_DEEP }}>
                      Lösung
                    </p>
                    <p className="mt-1.5 mb-5 text-[15px] leading-[1.62] flex-1" style={{ color: INK_SOFT }}>
                      {item.solution}
                    </p>

                    <div className="pt-4 border-t" style={{ borderColor: LINE_SOFT }}>
                      <p className="font-kanit font-medium text-[38px] leading-none text-abis-primary m-0">
                        {item.metricValue}
                      </p>
                      <p className="text-[13.5px] font-semibold mt-1.5 m-0" style={{ color: INK_SOFT }}>
                        {item.metricLabel}
                      </p>
                    </div>
                  </div>

                  {/* Kundenstimme — abgesetzt am Fuß der Karte */}
                  <figure className="m-0 px-6 py-6 md:px-[26px] border-t" style={{ backgroundColor: BG_ALT, borderColor: LINE }}>
                    <blockquote className="m-0 text-[15px] leading-[1.62] italic" style={{ color: "#2a3047" }}>
                      „{item.quote}“
                    </blockquote>
                    <figcaption className="mt-3 text-[13px]" style={{ color: INK_SOFT }}>
                      <span className="font-semibold text-abis-primary">{item.author}</span>
                      <br />
                      {item.role}
                    </figcaption>
                    <Link
                      href={item.href}
                      className="inline-block mt-4 text-[14.5px] font-semibold text-abis-primary hover:text-[#d97a1f] transition-colors"
                    >
                      Ganze Story lesen →
                    </Link>
                  </figure>
                </article>
              ))}
            </div>

            <Link
              href={pageData.cases.ctaHref}
              className="inline-flex items-center gap-2.5 mt-9 text-[15.5px] font-semibold text-abis-primary hover:text-[#d97a1f] transition-colors"
            >
              {pageData.cases.ctaLabel} →
            </Link>
          </div>
        </section>

        {/* 07 — DER EINSTIEG (primäre CTA-Sektion) */}
        <section
          id="einstieg"
          className="bg-gradient-to-b from-white to-[#ecf1fa] border-b"
          style={{ borderColor: LINE_BLUE }}
        >
          <div className={`${container} py-16 md:py-[104px]`}>
            <div className="max-w-[70ch]">
              <Eyebrow num="07" label={pageData.einstieg.eyebrow} />
              <SectionTitle className="md:text-[2.6rem]">{pageData.einstieg.title}</SectionTitle>
              <p className="text-lg leading-[1.65] mt-5" style={{ color: INK }}>
                {pageData.einstieg.paragraphs[0]}
              </p>
            </div>

            <div className="mt-11 grid md:grid-cols-[1.15fr_0.85fr] gap-8 items-start">
              {/* CTA-Karte — die Seite qualifiziert nicht selbst, sie übergibt
                  sauber an die Kursseite (Brief §1.4). */}
              <div
                className="bg-white rounded-3xl px-8 py-9 md:px-10 md:py-[38px] shadow-[0_24px_60px_rgba(0,31,92,0.12)] border"
                style={{ borderColor: LINE }}
              >
                <h3 className="font-kanit font-normal text-[26px] leading-[1.25] text-abis-primary m-0">
                  Platz im kostenfreien Kurs sichern
                </h3>
                <p className="mt-2 text-[15.5px] leading-relaxed" style={{ color: INK_SOFT }}>
                  {pageData.einstieg.paragraphs[1]}
                </p>
                <div className="flex flex-wrap gap-4 items-center mt-7">
                  <Link
                    href={kursHref}
                    onClick={() => trackKursCta("einstieg")}
                    className={`${primaryCtaClasses} text-[16.5px] px-[30px] py-4`}
                  >
                    {pageData.einstieg.primaryCta} <span aria-hidden="true">→</span>
                  </Link>
                  <span className="text-sm" style={{ color: INK_SOFT }}>
                    {pageData.einstieg.microcopy}
                  </span>
                </div>
                <p className="mt-6 pt-5 border-t text-[15.5px] m-0" style={{ borderColor: LINE_SOFT, color: INK_SOFT }}>
                  {pageData.einstieg.secondaryLead}{" "}
                  <Link
                    href={meetHref}
                    onClick={() => trackMeetCta("einstieg")}
                    className="font-semibold text-abis-primary border-b border-abis-orange hover:text-[#d97a1f] transition-colors"
                  >
                    {pageData.einstieg.secondaryCta}
                  </Link>
                </p>
              </div>

              {/* Terminkarte + Workshop-Bild */}
              <div className="grid gap-6">
                <aside className={`${darkPanel} rounded-3xl px-8 py-8 md:px-[34px] shadow-[0_24px_56px_rgba(0,31,92,0.24)]`}>
                  <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] m-0" style={{ color: BLUE_SOFT }}>
                    {pageData.einstieg.termCard.eyebrow}
                  </p>
                  <p className="font-kanit font-medium text-[26px] md:text-[30px] text-white mt-2 m-0">
                    {nextCourseLabel}
                  </p>
                  <p className="text-[15px] mt-1 m-0" style={{ color: BLUE_PALE }}>
                    {pageData.einstieg.termCard.details}
                  </p>
                  <p className="text-[15px] font-semibold text-abis-orange mt-2.5 m-0">
                    {pageData.einstieg.termCard.priceLabel}
                  </p>
                  <div className="grid gap-3 mt-5 pt-5 border-t border-white/15">
                    {pageData.einstieg.termCard.items.map((item) => (
                      <p key={item} className={`${checkRow} m-0`}>
                        <span className="text-abis-orange" aria-hidden="true">
                          ✓
                        </span>
                        <span>{item}</span>
                      </p>
                    ))}
                  </div>
                  <div className="mt-5 pt-[18px] border-t border-white/15 flex items-center gap-3">
                    <SpeakerAvatars speakers={pageData.speakers.items} size={40} />
                    <p className="text-[14.5px] m-0" style={{ color: BLUE_PALE }}>
                      Live von{" "}
                      <span className="text-white font-semibold">
                        {pageData.speakers.items.map((speaker) => speaker.name).join(" & ")}
                      </span>
                    </p>
                  </div>
                </aside>
                <div className="relative rounded-[20px] overflow-hidden shadow-[0_18px_44px_rgba(0,31,92,0.14)] aspect-[3/2]">
                  <Image
                    src={pageData.einstieg.image.src}
                    alt={pageData.einstieg.image.alt}
                    fill
                    sizes="(max-width: 768px) 100vw, 460px"
                    className="object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 07 — IHRE TRAINER */}
        <section id="trainer" className="bg-white border-b" style={{ borderColor: LINE_SOFT }}>
          <div className={`${container} py-16 md:py-[104px]`}>
            <Eyebrow num="08" label={pageData.speakers.eyebrow} />
            <SectionTitle className="max-w-[26em] md:text-[2.4rem]">{pageData.speakers.title}</SectionTitle>
            <p className="text-lg leading-[1.65] mt-[18px] max-w-[66ch]" style={{ color: INK }}>
              {pageData.speakers.lede}
            </p>

            <div
              className="mt-10 md:mt-12 grid gap-px border rounded-[22px] overflow-hidden"
              style={{ backgroundColor: LINE, borderColor: LINE }}
            >
              {pageData.speakers.items.map((speaker) => (
                <article
                  key={speaker.name}
                  className="bg-white p-7 md:p-9 grid md:grid-cols-[150px_1fr_260px] gap-7 md:gap-9 items-start"
                >
                  <div className="relative w-[120px] h-[120px] md:w-[150px] md:h-[150px] rounded-[20px] overflow-hidden" style={{ backgroundColor: BG_ALT }}>
                    <Image
                      src={speaker.image}
                      alt={speaker.imageAlt}
                      fill
                      sizes="150px"
                      className="object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-kanit font-normal text-[26px] text-abis-primary m-0">{speaker.name}</h3>
                    <p className="text-[14.5px] font-semibold mt-0.5 m-0" style={{ color: ACCENT_DEEP }}>
                      {speaker.role}
                    </p>
                    {"phone" in speaker && speaker.phone ? (
                      <a
                        href={`tel:${speaker.phone.replace(/[^+\d]/g, "")}`}
                        className="inline-block mt-2 text-[15px] font-semibold text-abis-primary hover:text-[#d97a1f] transition-colors"
                      >
                        {speaker.phone}
                      </a>
                    ) : null}
                    <p className="mt-3.5 mb-0 text-[16.5px] leading-[1.65]" style={{ color: INK_SOFT }}>
                      {speaker.bio}
                    </p>
                    <Link
                      href={speaker.profileHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-4 text-[15px] font-semibold text-abis-primary hover:text-[#d97a1f] transition-colors"
                    >
                      {speaker.profileLabel} →
                    </Link>
                  </div>
                  <div className="grid gap-2.5 rounded-2xl px-6 py-[22px]" style={{ backgroundColor: BG_ALT }}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] m-0" style={{ color: INK_MUTED }}>
                      Belege
                    </p>
                    {speaker.highlights.map((highlight) => (
                      <p
                        key={highlight}
                        className="grid grid-cols-[auto_1fr] gap-2.5 text-[15px] leading-normal m-0"
                        style={{ color: "#2a3047" }}
                      >
                        <span className="font-bold" style={{ color: GREEN }} aria-hidden="true">
                          ✓
                        </span>
                        <span>{highlight}</span>
                      </p>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* 08 — FAQ (Antworten stehen als <details> vollständig im HTML) */}
        <section id="faq" className="border-b" style={{ backgroundColor: BG_ALT, borderColor: LINE_SOFT }}>
          <div className={`${container} py-16 md:py-24`}>
            <div className="grid md:grid-cols-[0.8fr_1.2fr] gap-10 md:gap-16 items-start">
              <div className="md:sticky md:top-28">
                <Eyebrow num="09" label={pageData.faq.eyebrow} />
                <SectionTitle className="md:text-4xl">{pageData.faq.title}</SectionTitle>
                <p className="text-[16.5px] leading-[1.65] mt-4" style={{ color: INK_SOFT }}>
                  {pageData.faq.sideNote}
                </p>
                <Link
                  href={kursHref}
                  onClick={() => trackKursCta("faq")}
                  className="inline-flex items-center gap-2.5 mt-5 text-[15.5px] font-semibold text-abis-primary hover:text-[#d97a1f] transition-colors"
                >
                  {pageData.faq.sideCtaLabel} →
                </Link>
                <div className="hidden md:block relative mt-10 rounded-[20px] overflow-hidden shadow-[0_18px_44px_rgba(0,31,92,0.14)] aspect-[4/3]">
                  <Image
                    src="/assets/imagery/team-handshake-outdoor.png"
                    alt="Erstgespräch mit DBI Analytics — persönlicher Austausch statt Formular"
                    fill
                    sizes="(max-width: 768px) 100vw, 340px"
                    className="object-cover"
                  />
                </div>
              </div>
              <div className="grid gap-3">
                {pageData.faq.faqs.map((item) => (
                  <details
                    key={item.id}
                    className="group bg-white border rounded-2xl overflow-hidden"
                    style={{ borderColor: LINE }}
                    onToggle={(e) => {
                      if (item.id === "preis" && (e.target as HTMLDetailsElement).open) {
                        fireOnce("faq_preis");
                      }
                    }}
                  >
                    <summary className="cursor-pointer list-none flex gap-5 items-center px-6 py-5 md:px-[26px] md:py-[22px] font-kanit text-[17px] md:text-[19px] text-abis-primary hover:bg-[#f6f7fa] transition-colors">
                      <span className="flex-1">{item.question}</span>
                      <span
                        className="text-abis-orange text-[22px] leading-none group-open:rotate-45 transition-transform"
                        aria-hidden="true"
                      >
                        +
                      </span>
                    </summary>
                    <p className="px-6 md:px-[26px] pb-6 pt-0 m-0 text-base leading-[1.7]" style={{ color: INK_SOFT }}>
                      {item.answer}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ABSCHLUSS */}
        <section className={darkPanel}>
          <div className={`${container} py-16 md:py-24 text-center`}>
            <h2 className="font-kanit font-medium text-[2rem] md:text-[2.6rem] leading-[1.16] text-white m-0 mx-auto max-w-[22em]">
              {pageData.abschluss.title}
            </h2>
            <p className="text-lg leading-relaxed mt-[18px] mx-auto max-w-[56ch]" style={{ color: BLUE_SOFT }}>
              {pageData.abschluss.subline}
            </p>
            <div className="flex flex-wrap gap-3.5 justify-center mt-8">
              <Link
                href={kursHref}
                onClick={() => trackKursCta("abschluss")}
                className={`${primaryCtaClasses} text-[16.5px] px-[30px] py-[17px]`}
              >
                {pageData.abschluss.primaryCta} <span aria-hidden="true">→</span>
              </Link>
              <Link
                href={meetHref}
                onClick={() => trackMeetCta("abschluss")}
                className="inline-flex items-center justify-center gap-3 bg-white/10 text-white font-semibold text-[16.5px] px-[30px] py-[17px] rounded-[14px] border-[1.5px] border-white/30 hover:bg-white/20 transition-colors"
              >
                {pageData.abschluss.secondaryCta} <span aria-hidden="true">→</span>
              </Link>
            </div>
            <p className="text-[14.5px] mt-5 m-0" style={{ color: BLUE_SOFT }}>
              {pageData.abschluss.note}
            </p>
          </div>
        </section>

        {/* 09 — VERWANDTE LEISTUNGEN: bidirektionale Cluster-Verlinkung, je
            Eintrag an die zuständige Stufe gehängt. */}
        <section className="border-b" style={{ backgroundColor: BG_ALT, borderColor: LINE_SOFT }}>
          <div className={`${container} py-16 md:py-24`}>
            <Eyebrow num="10" label={pageData.related.eyebrow} />
            <SectionTitle className="md:text-4xl">{pageData.related.title}</SectionTitle>
            <p className="text-[17px] leading-[1.65] mt-4 max-w-[60ch]" style={{ color: INK }}>
              {pageData.related.lede}
            </p>
            <div
              className="mt-9 grid md:grid-cols-2 gap-px border rounded-[20px] overflow-hidden"
              style={{ backgroundColor: LINE, borderColor: LINE }}
            >
              {pageData.related.links.map((link) => {
                const isKurs = link.href === KURS_ROUTE;
                return (
                  <Link
                    key={link.href}
                    href={isKurs ? kursHref : link.href}
                    onClick={isKurs ? () => trackKursCta("related") : undefined}
                    className={`px-7 py-6 md:px-7 md:py-[26px] grid grid-cols-[1fr_auto] gap-4 items-center transition-all ${
                      isKurs
                        ? "bg-[#ecf1fa] hover:bg-[#d6dff0]"
                        : "bg-white hover:shadow-[inset_3px_0_0_#ffa346]"
                    }`}
                  >
                    <span className="block">
                      <span
                        className="block text-[11px] font-semibold uppercase tracking-[0.14em]"
                        style={{ color: ACCENT_DEEP }}
                      >
                        {link.stage}
                      </span>
                      <span className="block font-kanit text-[21px] text-abis-primary mt-1.5">{link.label}</span>
                      <span className="block text-[15px] leading-relaxed mt-1.5" style={{ color: INK_SOFT }}>
                        {link.teaser}
                      </span>
                    </span>
                    <span className="text-abis-primary text-lg" aria-hidden="true">
                      →
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* End of page: hand-over into the Agentic BI Guide (guide-links.json) */}
        {guideSlot}
      </main>

      <FooterPublic />

      {/* STICKY-BAR ab Ende Hero — dunkel und frosted wie im Referenz-Design */}
      {showStickyBar && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-[rgba(0,31,92,0.96)] backdrop-blur border-t border-white/15">
          <div className={`${container} py-3.5 flex flex-wrap gap-4 items-center`}>
            <SpeakerAvatars speakers={pageData.speakers.items} size={36} className="hidden sm:flex shrink-0" />
            <p className="m-0 text-[15px] text-white flex items-center gap-2 min-w-0">
              <span className="font-semibold whitespace-nowrap">
                {pageData.stickyBar.text} {nextCourseLabel}
              </span>
              <span className="hidden sm:inline" style={{ color: BLUE_SOFT }}>
                · {pageData.stickyBar.meta}
              </span>
            </p>
            <Link
              href={kursHref}
              onClick={() => trackKursCta("sticky")}
              className="ml-auto shrink-0 inline-flex items-center gap-2.5 bg-abis-orange text-abis-primary font-semibold text-[15px] px-[22px] py-3 rounded-xl hover:bg-[#f08a2c] transition-colors"
            >
              {pageData.stickyBar.ctaLabel} <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
